import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import {
  BackendResponse,
  DatastoreCollection,
  HourlyContract,
} from '@freelancer/datastore';
import { ModalRef } from '@freelancer/ui';
import { BannerAlertType } from '@freelancer/ui/banner-alert';
import { ButtonColor, ButtonSize } from '@freelancer/ui/button';
import { HeadingSize, HeadingType } from '@freelancer/ui/heading';
import { Margin } from '@freelancer/ui/margin';
import { FontSize, FontType } from '@freelancer/ui/text';
import * as Rx from 'rxjs';
import { take } from 'rxjs/operators';

@Component({
  template: `
    <fl-heading
      i18n="End billing modal heading"
      [size]="HeadingSize.XSMALL"
      [headingType]="HeadingType.H4"
      [flMarginBottom]="Margin.SMALL"
    >
      End Billing?
    </fl-heading>

    <fl-text
      i18n="End billing modal information text"
      [size]="FontSize.MID"
      [fontType]="FontType.PARAGRAPH"
      [flMarginBottom]="Margin.SMALL"
    >
      {{ displayName }} will not be able to track any more hours. Any remaining
      hours will be invoiced right away.
    </fl-text>

    <fl-banner-alert
      *ngIf="(endBillingPromise | async)?.status === 'error'"
      i18n="End billing error"
      [type]="BannerAlertType.ERROR"
      [flMarginBottom]="Margin.SMALL"
    >
      Something went wrong when ending the billing. Please contract
      support@freelancer.com
      <ng-container *ngIf="(endBillingPromise | async)?.requestId">
        with requestId: {{ (endBillingPromise | async)?.requestId }}
      </ng-container>
    </fl-banner-alert>

    <fl-bit class="Buttons">
      <fl-button
        i18n="End billing button"
        [flMarginRight]="Margin.XSMALL"
        [color]="ButtonColor.SECONDARY"
        [size]="ButtonSize.SMALL"
        [busy]="endBillingPromise && (endBillingPromise | async) === null"
        (click)="endBilling()"
      >
        End Billing
      </fl-button>

      <fl-button
        i18n="Close modal button"
        [color]="ButtonColor.DEFAULT"
        [size]="ButtonSize.SMALL"
        (click)="close()"
      >
        Back
      </fl-button>
    </fl-bit>
  `,
  styleUrls: ['./project-end-billing-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjectEndBillingModalComponent {
  HeadingSize = HeadingSize;
  HeadingType = HeadingType;
  FontType = FontType;
  FontSize = FontSize;
  ButtonColor = ButtonColor;
  ButtonSize = ButtonSize;
  BannerAlertType = BannerAlertType;
  Margin = Margin;

  @Input() displayName: string;
  @Input() hourlyContractsCollection: DatastoreCollection<'hourlyContracts'>;
  @Input() hourlyContract$: Rx.Observable<HourlyContract>;

  endBillingPromise: Promise<BackendResponse>;

  constructor(private modalRef: ModalRef<ProjectEndBillingModalComponent>) {}

  endBilling() {
    this.endBillingPromise = this.hourlyContract$
      .pipe(take(1))
      .toPromise()
      .then(hourlyContract => {
        if (hourlyContract && this.hourlyContractsCollection) {
          const contractId = hourlyContract.id;

          return this.hourlyContractsCollection.update(contractId, {
            timeTrackingStopped: hourlyContract.timeTrackingStopped,
          });
        }

        return Promise.resolve({
          status: 'error',
          errorCode: 'UNKNOWN_ERROR',
        } as BackendResponse);
      })
      .then(res => {
        if (res.status === 'success') {
          this.close();
        }
        return res;
      });
  }

  close() {
    this.modalRef.close();
  }
}
